import type { SubAppModuleFederation } from '@ucc/admin-module-federation-utils'
import {
  ADD_ONS_ROUTE,
  BILLING_ROUTE,
  CALL_QUEUES_ROUTE,
  DASHBOARD_ROUTE,
  DEVICES_ROUTE,
  DIAL_PLANS_ROUTE,
  GTC_BILLING_ROUTE,
  INTEGRATIONS_ROUTE,
  NETWORK_ROUTE,
  NUMBERS_ROUTE,
  ORDERS_ROUTE,
  OVERVIEW_ROUTE,
  PEOPLE_ROUTE,
  PHONES_ROUTE,
  PLACES_ROUTE,
  PRODUCTS_ROUTE,
  SETTINGS_ROUTE,
  SMS_CAMPAIGN_ROUTE,
  TENANTS_ROUTE,
} from '@ucc/unified-admin-common'

import { ZERO_TRUST_ROUTE } from 'utils/constants'
import { branchNameSubdomain, branchNameSubfolder } from './featureBranch'

const commonBase = {
  uccUnifiedAdminCCConfiguration: {
    appName: '@ucc/cc-config',
    remoteEntryFileName: 'ccConfigurationEntry.js',
    routes: [CALL_QUEUES_ROUTE, 'inbox/chat-assistants'],
    scope: 'ccConfiguration',
    enabled: true,
  },
  uccUnifiedAdminCore: {
    appName: '@ucc/unified-admin-core',
    remoteEntryFileName: 'unifiedAdminCoreEntry.js',
    routes: [PEOPLE_ROUTE, SETTINGS_ROUTE, PRODUCTS_ROUTE, 'reports', 'activity'],
    scope: 'unifiedAdminCore',
    enabled: true,
  },
  uccUnifiedAdminVoice: {
    appName: '@ucc/unified-admin-jive',
    remoteEntryFileName: 'unifiedAdminVoiceEntry.js',
    routes: [PHONES_ROUTE],
    scope: 'unifiedAdminVoice',
    enabled: true,
  },
  uccUnifiedAdminOrdering: {
    appName: '@ucc/unified-admin-ordering',
    remoteEntryFileName: 'unifiedAdminOrderingEntry.js',
    routes: [ORDERS_ROUTE, NUMBERS_ROUTE],
    scope: 'unifiedAdminOrdering',
    enabled: true,
  },
  uccUnifiedAdminGuidedSetup: {
    appName: '@ucc/unified-admin-guided-setup',
    remoteEntryFileName: 'unifiedAdminGuidedSetupEntry.js',
    scope: 'unifiedAdminGuidedSetup',
    enabled: true,
  },
  uccUnifiedAdminDevices: {
    appName: '@ucc/unified-admin-devices',
    remoteEntryFileName: 'unifiedAdminDevicesEntry.js',
    routes: [DEVICES_ROUTE],
    scope: 'unifiedAdminDevices',
    enabled: true,
  },
  uccUnifiedAdminLocations: {
    appName: '@ucc/unified-admin-locations',
    remoteEntryFileName: 'unifiedAdminLocationsEntry.js',
    routes: [PLACES_ROUTE],
    scope: 'unifiedAdminLocations',
    enabled: true,
  },
  uccUnifiedAdminDynamicSetup: {
    appName: '@ucc/unified-admin-dynamic-setup',
    remoteEntryFileName: 'unifiedAdminDynamicSetupEntry.js',
    scope: 'unifiedAdminDynamicSetup',
    enabled: true,
  },
  uccUnifiedAdminSmsCampaign: {
    appName: '@ucc/unified-admin-sms-campaign',
    remoteEntryFileName: 'unifiedAdminSmsCampaignEntry.js',
    routes: [SMS_CAMPAIGN_ROUTE],
    scope: 'unifiedAdminSmsCampaign',
    enabled: true,
  },
  uccCopilot: {
    appName: '@ucc/admin-copilot-ui',
    remoteEntryFileName: 'copilotUiEntry.js',
    scope: 'copilotUi',
    enabled: true,
  },
  uccUnifiedAdminNetworkMeasurements: {
    appName: '@ucc/unified-admin-network-measurements',
    remoteEntryFileName: 'unifiedAdminNetworkMeasurementsEntry.js',
    routes: [NETWORK_ROUTE],
    scope: 'unifiedAdminNetworkMeasurements',
    enabled: true,
  },
  uccCCTopicFlags: {
    appName: '@ucc/gtc-cc-topic-flag',
    remoteEntryFileName: 'remoteEntry.js',
    scope: '@ucc/gtc-cc-topic-flag',
    enabled: true,
  },
  uccUnifiedAdminDial: {
    appName: '@ucc/unified-admin-dialplaneditor',
    remoteEntryFileName: 'unifiedAdminDialPlanEntry.js',
    routes: [DIAL_PLANS_ROUTE],
    scope: 'unifiedAdminDialPlan',
    enabled: true,
  },
  uccUnifiedAdminGtcBilling: {
    appName: '@ucc/unified-admin-gtc-billing',
    remoteEntryFileName: 'unifiedAdminGtcBillingEntry.js',
    routes: [GTC_BILLING_ROUTE],
    scope: 'unifiedAdminGtcBilling',
    enabled: true,
  },
  uccUnifiedAdminVirtualReceptionist: {
    appName: '@ucc/unified-admin-virtual-receptionist',
    remoteEntryFileName: 'unifiedAdminVirtualReceptionistEntry.js',
    scope: 'unifiedAdminVirtualReceptionist',
    routes: [
      'digital-workforce/ai-receptionists',
      'digital-workforce/knowledge',
      'digital-workforce/info-capture',
      'digital-workforce/getting-started',
    ],
    enabled: true,
  },
  uccUnifiedAdminDashboard: {
    appName: '@ucc/unified-admin-dashboard',
    remoteEntryFileName: 'unifiedAdminDashboardEntry.js',
    routes: [DASHBOARD_ROUTE],
    scope: 'unifiedAdminDashboard',
    enabled: true,
  },
  uccUnifiedAdminProductsExploration: {
    appName: '@ucc/unified-admin-products-exploration',
    remoteEntryFileName: 'unifiedAdminProductsExplorationEntry.js',
    scope: 'unifiedAdminProductsExploration',
    routes: [ADD_ONS_ROUTE],
    enabled: true,
  },
  uccUnifiedAdminIntegrations: {
    appName: '@ucc/unified-admin-integrations',
    remoteEntryFileName: 'unifiedAdminIntegrationsEntry.js',
    scope: 'unifiedAdminIntegrations',
    routes: [INTEGRATIONS_ROUTE],
    enabled: true,
  },
  uccUnifiedAdminContacts: {
    appName: '@contacts',
    remoteEntryFileName: 'contactsEntry.js',
    scope: 'contacts',
    routes: [`contacts`, `contacts/all-contacts`, `contacts/group`, `contacts/custom-fields`],
    enabled: true,
  },
  uccUnifiedAdminCart: {
    appName: '@ucc/unified-admin-cart',
    remoteEntryFileName: 'unifiedAdminCartEntry.js',
    scope: 'unifiedAdminCart',
    enabled: true,
  },
  uccUnifiedAdminBilling: {
    appName: '@ucc/unified-admin-payment',
    remoteEntryFileName: 'unifiedAdminBillingEntry.js',
    routes: [BILLING_ROUTE],
    scope: 'unifiedAdminBilling',
    enabled: true,
  },
  uccUnifiedAdminZeroTrust: {
    appName: '@ucc/unified-admin-zero-trust',
    remoteEntryFileName: 'unifiedAdminZeroTrustEntry.js',
    routes: [`${SETTINGS_ROUTE}/${ZERO_TRUST_ROUTE}`],
    scope: 'unifiedAdminZeroTrust',
    enabled: true,
  },
  uccUnifiedAdminMedia: {
    appName: '@ucc/unified-admin-media',
    remoteEntryFileName: 'unifiedAdminMediaEntry.js',
    routes: [`${PHONES_ROUTE}/audio-library`],
    scope: 'unifiedAdminMedia',
    enabled: true,
  },
  uccUnifiedAdminTenants: {
    appName: '@ucc/unified-admin-tenants',
    remoteEntryFileName: 'unifiedAdminTenantsEntry.js',
    routes: [`${TENANTS_ROUTE}`],
    scope: 'unifiedAdminTenants',
    enabled: true,
  },
  uccUnifiedAdminLaunchpad: {
    appName: '@ucc/unified-admin-launchpad',
    remoteEntryFileName: 'unifiedAdminLaunchpadEntry.js',
    scope: 'unifiedAdminLaunchpad',
    routes: [OVERVIEW_ROUTE],
    enabled: true,
  },
  uccUnifiedAdminInbox: {
    appName: '@dce/web-components-loader',
    remoteEntryFileName: 'dceAdminEntry.js',
    scope: 'dceAdmin',
    routes: [`${SETTINGS_ROUTE}/customer-engagement`, `inbox/customer-engagement`, `inbox/inboxes`],
    enabled: true,
  },
}

const subAppsModuleFederationConfig: {
  [key in 'local' | 'development' | 'staging' | 'production']: {
    [key in string]: SubAppModuleFederation
  }
} = {
  local: {
    uccUnifiedAdminCCConfiguration: {
      ...commonBase.uccUnifiedAdminCCConfiguration,
      url: `//localhost:7134`,
      enabled: false,
    },
    uccUnifiedAdminCore: {
      ...commonBase.uccUnifiedAdminCore,
      url: '//localhost:7107',
      enabled: false,
    },
    uccUnifiedAdminVoice: {
      ...commonBase.uccUnifiedAdminVoice,
      url: '//localhost:7101',
      enabled: false,
    },
    uccUnifiedAdminOrdering: {
      ...commonBase.uccUnifiedAdminOrdering,
      url: '//localhost:7102',
      enabled: false,
    },
    uccUnifiedAdminGuidedSetup: {
      ...commonBase.uccUnifiedAdminGuidedSetup,
      url: '//localhost:7103',
      enabled: false,
    },
    uccUnifiedAdminDevices: {
      ...commonBase.uccUnifiedAdminDevices,
      url: '//localhost:7106',
      enabled: false,
    },
    uccUnifiedAdminLocations: {
      ...commonBase.uccUnifiedAdminLocations,
      url: '//localhost:7111',
      enabled: false,
    },
    uccUnifiedAdminDynamicSetup: {
      ...commonBase.uccUnifiedAdminDynamicSetup,
      url: '//localhost:7112',
      enabled: false,
    },
    uccUnifiedAdminSmsCampaign: {
      ...commonBase.uccUnifiedAdminSmsCampaign,
      url: '//localhost:7115',
      enabled: false,
    },
    uccCopilot: {
      ...commonBase.uccCopilot,
      url: '//localhost:7120',
      enabled: false,
    },
    uccUnifiedAdminNetworkMeasurements: {
      ...commonBase.uccUnifiedAdminNetworkMeasurements,
      url: '//localhost:7110',
      enabled: false,
    },
    uccCCTopicFlags: {
      ...commonBase.uccCCTopicFlags,
      url: '//localhost:7135/app/gotoadmin-cc-topic-flag/',
      enabled: false,
    },
    uccUnifiedAdminDial: {
      ...commonBase.uccUnifiedAdminDial,
      url: '//localhost:7113',
      enabled: false,
    },
    uccUnifiedAdminGtcBilling: {
      ...commonBase.uccUnifiedAdminGtcBilling,
      url: 'https://localhost:7121',
      enabled: false,
    },
    uccUnifiedAdminVirtualReceptionist: {
      ...commonBase.uccUnifiedAdminVirtualReceptionist,
      url: '//localhost:7117',
      enabled: false,
    },
    uccUnifiedAdminDashboard: {
      ...commonBase.uccUnifiedAdminDashboard,
      url: '//localhost:7105',
      enabled: false,
    },
    uccUnifiedAdminProductsExploration: {
      ...commonBase.uccUnifiedAdminProductsExploration,
      url: '//localhost:7114',
      enabled: false,
    },
    uccUnifiedAdminIntegrations: {
      ...commonBase.uccUnifiedAdminIntegrations,
      url: '//localhost:7108',
      enabled: false,
    },
    uccUnifiedAdminContacts: {
      ...commonBase.uccUnifiedAdminContacts,
      url: '//localhost:7122',
      enabled: false,
    },
    uccUnifiedAdminCart: {
      ...commonBase.uccUnifiedAdminCart,
      url: '//localhost:7116',
      enabled: false,
    },
    uccUnifiedAdminBilling: {
      ...commonBase.uccUnifiedAdminBilling,
      url: '//localhost:7109',
      enabled: false,
    },
    uccUnifiedAdminZeroTrust: {
      ...commonBase.uccUnifiedAdminZeroTrust,
      url: '//localhost:7199',
      enabled: false,
    },
    uccUnifiedAdminMedia: {
      ...commonBase.uccUnifiedAdminMedia,
      url: '//localhost:7119',
      enabled: false,
    },
    uccUnifiedAdminTenants: {
      ...commonBase.uccUnifiedAdminTenants,
      url: '//localhost:7169',
      enabled: false,
    },
    uccUnifiedAdminLaunchpad: {
      ...commonBase.uccUnifiedAdminLaunchpad,
      url: '//localhost:7123',
      enabled: false,
    },
    uccUnifiedAdminInbox: {
      ...commonBase.uccUnifiedAdminInbox,
      url: '//localhost:7188/experiences/engagements/wc-loader',
      enabled: false,
    },
  },
  development: {
    uccUnifiedAdminCCConfiguration: {
      ...commonBase.uccUnifiedAdminCCConfiguration,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-cc-config`,
    },
    uccUnifiedAdminCore: {
      ...commonBase.uccUnifiedAdminCore,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-core/${branchNameSubfolder}`,
    },
    uccUnifiedAdminVoice: {
      ...commonBase.uccUnifiedAdminVoice,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-voice/${branchNameSubfolder}`,
    },
    uccUnifiedAdminOrdering: {
      ...commonBase.uccUnifiedAdminOrdering,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-ordering/${branchNameSubfolder}`,
    },
    uccUnifiedAdminGuidedSetup: {
      ...commonBase.uccUnifiedAdminGuidedSetup,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-guided-setup/${branchNameSubfolder}`,
    },
    uccUnifiedAdminDevices: {
      ...commonBase.uccUnifiedAdminDevices,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-devices/${branchNameSubfolder}`,
    },
    uccUnifiedAdminLocations: {
      ...commonBase.uccUnifiedAdminLocations,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-locations/${branchNameSubfolder}`,
    },
    uccUnifiedAdminDynamicSetup: {
      ...commonBase.uccUnifiedAdminDynamicSetup,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-dynamic-setup/${branchNameSubfolder}`,
    },
    uccUnifiedAdminSmsCampaign: {
      ...commonBase.uccUnifiedAdminSmsCampaign,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-sms-campaign/${branchNameSubfolder}`,
    },
    uccCopilot: {
      ...commonBase.uccCopilot,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/admin-copilot-ui/${branchNameSubfolder}`,
    },
    uccUnifiedAdminNetworkMeasurements: {
      ...commonBase.uccUnifiedAdminNetworkMeasurements,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-network-measurements/${branchNameSubfolder}`,
    },
    uccCCTopicFlags: {
      ...commonBase.uccCCTopicFlags,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-cc-topic-flag/${branchNameSubfolder}`,
    },
    uccUnifiedAdminDial: {
      ...commonBase.uccUnifiedAdminDial,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-dialplan/${branchNameSubfolder}`,
    },
    uccUnifiedAdminGtcBilling: {
      ...commonBase.uccUnifiedAdminGtcBilling,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-gtc-billing/${branchNameSubfolder}`,
    },
    uccUnifiedAdminVirtualReceptionist: {
      ...commonBase.uccUnifiedAdminVirtualReceptionist,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-virtual-receptionist/${branchNameSubfolder}`,
    },
    uccUnifiedAdminDashboard: {
      ...commonBase.uccUnifiedAdminDashboard,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-dashboard/${branchNameSubfolder}`,
    },
    uccUnifiedAdminProductsExploration: {
      ...commonBase.uccUnifiedAdminProductsExploration,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-products-exploration/${branchNameSubfolder}`,
    },
    uccUnifiedAdminIntegrations: {
      ...commonBase.uccUnifiedAdminIntegrations,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-integrations/${branchNameSubfolder}`,
    },
    uccUnifiedAdminContacts: {
      ...commonBase.uccUnifiedAdminContacts,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-contacts`,
    },
    uccUnifiedAdminCart: {
      ...commonBase.uccUnifiedAdminCart,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-cart/${branchNameSubfolder}`,
    },
    uccUnifiedAdminBilling: {
      ...commonBase.uccUnifiedAdminBilling,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-payment/${branchNameSubfolder}`,
    },
    uccUnifiedAdminZeroTrust: {
      ...commonBase.uccUnifiedAdminZeroTrust,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-zero-trust/${branchNameSubfolder}`,
    },
    uccUnifiedAdminMedia: {
      ...commonBase.uccUnifiedAdminMedia,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-media/${branchNameSubfolder}`,
    },
    uccUnifiedAdminTenants: {
      ...commonBase.uccUnifiedAdminTenants,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-tenants/${branchNameSubfolder}`,
    },
    uccUnifiedAdminLaunchpad: {
      ...commonBase.uccUnifiedAdminLaunchpad,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/app/gotoadmin-launchpad/${branchNameSubfolder}`,
    },
    uccUnifiedAdminInbox: {
      ...commonBase.uccUnifiedAdminInbox,
      url: `//${branchNameSubdomain}admin.dev.gtc.goto.com/experiences/engagements/wc-loader`,
    },
  },
  staging: {
    uccUnifiedAdminCCConfiguration: {
      ...commonBase.uccUnifiedAdminCCConfiguration,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-cc-config',
    },
    uccUnifiedAdminCore: {
      ...commonBase.uccUnifiedAdminCore,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-core`,
    },
    uccUnifiedAdminVoice: {
      ...commonBase.uccUnifiedAdminVoice,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-voice`,
    },
    uccUnifiedAdminOrdering: {
      ...commonBase.uccUnifiedAdminOrdering,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-ordering',
    },
    uccUnifiedAdminGuidedSetup: {
      ...commonBase.uccUnifiedAdminGuidedSetup,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-guided-setup',
    },
    uccUnifiedAdminDevices: {
      ...commonBase.uccUnifiedAdminDevices,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-devices',
    },
    uccUnifiedAdminLocations: {
      ...commonBase.uccUnifiedAdminLocations,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-locations`,
    },
    uccUnifiedAdminDynamicSetup: {
      ...commonBase.uccUnifiedAdminDynamicSetup,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-dynamic-setup',
    },
    uccUnifiedAdminSmsCampaign: {
      ...commonBase.uccUnifiedAdminSmsCampaign,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-sms-campaign',
    },
    uccCopilot: {
      ...commonBase.uccCopilot,
      url: '//admin.staging.gtc.goto.com/app/admin-copilot-ui',
    },
    uccUnifiedAdminNetworkMeasurements: {
      ...commonBase.uccUnifiedAdminNetworkMeasurements,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-network-measurements',
    },
    uccCCTopicFlags: {
      ...commonBase.uccCCTopicFlags,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-cc-topic-flag`,
    },
    uccUnifiedAdminDial: {
      ...commonBase.uccUnifiedAdminDial,
      url: 'https://admin.staging.gtc.goto.com/app/gotoadmin-dialplan',
    },
    uccUnifiedAdminGtcBilling: {
      ...commonBase.uccUnifiedAdminGtcBilling,
      url: 'https://admin.staging.gtc.goto.com/app/gotoadmin-gtc-billing',
    },
    uccUnifiedAdminVirtualReceptionist: {
      ...commonBase.uccUnifiedAdminVirtualReceptionist,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-virtual-receptionist`,
    },
    uccUnifiedAdminDashboard: {
      ...commonBase.uccUnifiedAdminDashboard,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-dashboard`,
    },
    uccUnifiedAdminProductsExploration: {
      ...commonBase.uccUnifiedAdminProductsExploration,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-products-exploration`,
    },
    uccUnifiedAdminIntegrations: {
      ...commonBase.uccUnifiedAdminIntegrations,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-integrations`,
    },
    uccUnifiedAdminContacts: {
      ...commonBase.uccUnifiedAdminContacts,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-contacts`,
    },
    uccUnifiedAdminCart: {
      ...commonBase.uccUnifiedAdminCart,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-cart',
    },
    uccUnifiedAdminBilling: {
      ...commonBase.uccUnifiedAdminBilling,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-payment',
    },
    uccUnifiedAdminZeroTrust: {
      ...commonBase.uccUnifiedAdminZeroTrust,
      url: `//admin.staging.gtc.goto.com/app/gotoadmin-zero-trust`,
    },
    uccUnifiedAdminMedia: {
      ...commonBase.uccUnifiedAdminMedia,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-media',
    },
    uccUnifiedAdminTenants: {
      ...commonBase.uccUnifiedAdminTenants,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-tenants',
    },
    uccUnifiedAdminLaunchpad: {
      ...commonBase.uccUnifiedAdminLaunchpad,
      url: '//admin.staging.gtc.goto.com/app/gotoadmin-launchpad',
    },
    uccUnifiedAdminInbox: {
      ...commonBase.uccUnifiedAdminInbox,
      url: '//admin.staging.gtc.goto.com/experiences/engagements/wc-loader',
    },
  },
  production: {
    uccUnifiedAdminCCConfiguration: {
      ...commonBase.uccUnifiedAdminCCConfiguration,
      url: '//admin.goto.com/app/gotoadmin-cc-config',
    },
    uccUnifiedAdminCore: {
      ...commonBase.uccUnifiedAdminCore,
      url: `//admin.goto.com/app/gotoadmin-core`,
    },
    uccUnifiedAdminVoice: {
      ...commonBase.uccUnifiedAdminVoice,
      url: `//admin.goto.com/app/gotoadmin-voice`,
    },
    uccUnifiedAdminOrdering: {
      ...commonBase.uccUnifiedAdminOrdering,
      url: '//admin.goto.com/app/gotoadmin-ordering',
    },
    uccUnifiedAdminGuidedSetup: {
      ...commonBase.uccUnifiedAdminGuidedSetup,
      url: '//admin.goto.com/app/gotoadmin-guided-setup',
    },
    uccUnifiedAdminDevices: {
      ...commonBase.uccUnifiedAdminDevices,
      url: '//admin.goto.com/app/gotoadmin-devices',
    },
    uccUnifiedAdminLocations: {
      ...commonBase.uccUnifiedAdminLocations,
      url: `//admin.goto.com/app/gotoadmin-locations`,
    },
    uccUnifiedAdminDynamicSetup: {
      ...commonBase.uccUnifiedAdminDynamicSetup,
      url: '//admin.goto.com/app/gotoadmin-dynamic-setup',
    },
    uccUnifiedAdminSmsCampaign: {
      ...commonBase.uccUnifiedAdminSmsCampaign,
      url: '//admin.goto.com/app/gotoadmin-sms-campaign',
    },
    uccCopilot: {
      ...commonBase.uccCopilot,
      url: '//admin.goto.com/app/admin-copilot-ui',
    },
    uccUnifiedAdminNetworkMeasurements: {
      ...commonBase.uccUnifiedAdminNetworkMeasurements,
      url: '//admin.goto.com/app/gotoadmin-network-measurements',
    },
    uccCCTopicFlags: {
      ...commonBase.uccCCTopicFlags,
      url: `//admin.goto.com/app/gotoadmin-cc-topic-flag`,
    },
    uccUnifiedAdminDial: {
      ...commonBase.uccUnifiedAdminDial,
      url: 'https://admin.goto.com/app/gotoadmin-dialplan',
    },
    uccUnifiedAdminGtcBilling: {
      ...commonBase.uccUnifiedAdminGtcBilling,
      url: 'https://admin.goto.com/app/gotoadmin-gtc-billing',
    },
    uccUnifiedAdminVirtualReceptionist: {
      ...commonBase.uccUnifiedAdminVirtualReceptionist,
      url: `//admin.goto.com/app/gotoadmin-virtual-receptionist`,
    },
    uccUnifiedAdminDashboard: {
      ...commonBase.uccUnifiedAdminDashboard,
      url: `//admin.goto.com/app/gotoadmin-dashboard`,
    },
    uccUnifiedAdminProductsExploration: {
      ...commonBase.uccUnifiedAdminProductsExploration,
      url: `//admin.goto.com/app/gotoadmin-products-exploration`,
    },
    uccUnifiedAdminIntegrations: {
      ...commonBase.uccUnifiedAdminIntegrations,
      url: `//admin.goto.com/app/gotoadmin-integrations`,
    },
    uccUnifiedAdminContacts: {
      ...commonBase.uccUnifiedAdminContacts,
      url: `//admin.goto.com/app/gotoadmin-contacts`,
    },
    uccUnifiedAdminCart: {
      ...commonBase.uccUnifiedAdminCart,
      url: `//admin.goto.com/app/gotoadmin-cart`,
    },
    uccUnifiedAdminBilling: {
      ...commonBase.uccUnifiedAdminBilling,
      url: '//admin.goto.com/app/gotoadmin-payment',
    },
    uccUnifiedAdminZeroTrust: {
      ...commonBase.uccUnifiedAdminZeroTrust,
      url: `//admin.goto.com/app/gotoadmin-zero-trust`,
    },
    uccUnifiedAdminMedia: {
      ...commonBase.uccUnifiedAdminMedia,
      url: '//admin.goto.com/app/gotoadmin-media',
    },
    uccUnifiedAdminTenants: {
      ...commonBase.uccUnifiedAdminTenants,
      url: '//admin.goto.com/app/gotoadmin-tenants',
    },
    uccUnifiedAdminLaunchpad: {
      ...commonBase.uccUnifiedAdminLaunchpad,
      url: '//admin.goto.com/app/gotoadmin-launchpad',
    },
    uccUnifiedAdminInbox: {
      ...commonBase.uccUnifiedAdminInbox,
      url: '//admin.goto.com/experiences/engagements/wc-loader',
    },
  },
}

export default subAppsModuleFederationConfig
